(() => {
  class ModernPillHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.bar = this.querySelector('[data-mph-bar]');
      this.megaStage = this.querySelector('[data-mph-mega-stage]');
      this.hoverBridge = this.querySelector('[data-mph-hover-bridge]');
      this.triggers = [...this.querySelectorAll('[data-mph-trigger]')];
      this.panels = [...this.querySelectorAll('[data-mph-panel]')];
      this.searchStage = this.querySelector('[data-mph-search-stage]');
      this.searchInput = this.querySelector('[data-mph-search-input]');
      this.searchResults = this.querySelector('[data-mph-search-results]');
      this.searchOutput = this.querySelector('[data-mph-search-output]');
      this.searchStatus = this.querySelector('[data-mph-search-status]');
      this.searchButtons = [...this.querySelectorAll('[data-mph-search-open]')];
      this.drawer = this.querySelector('[data-mph-mobile-drawer]');
      this.drawerButton = this.querySelector('[data-mph-drawer-open]');
      this.closeTimer = null;
      this.searchTimer = null;
      this.searchAbortController = null;
      this.searchCache = new Map();
      this.brandPanelLoads = new Map();
      this.lastDrawerTrigger = null;

      this.bindMegaMenu();
      this.bindTabs();
      this.bindSearch();
      this.bindMobileDrawer();
      this.bindGlobalEvents();
      this.bindNavigationFit();
    }

    bindNavigationFit() {
      this.updateNavigationFit();
      document.fonts?.ready.then(() => this.updateNavigationFit());
    }

    updateNavigationFit() {
      this.classList.remove('mph--compact', 'mph--wrap');
      if (window.innerWidth < 1280) return;
      const nav = this.querySelector('.mph__desktop-nav');
      const list = this.querySelector('.mph__desktop-list');
      if (!nav || !list) return;
      const links = [...list.querySelectorAll('.mph__desktop-link')];
      if (!links.length) return;
      const bounds = nav.getBoundingClientRect();
      const first = (links[0].querySelector('.mph__nav-sign > span') || links[0]).getBoundingClientRect();
      const lastLink = links[links.length - 1];
      const last = (lastLink.querySelector('.mph__nav-sign > span') || lastLink).getBoundingClientRect();
      this.classList.toggle('mph--wrap', first.left < bounds.left - 1 || last.right > bounds.right + 1);
    }

    bindMegaMenu() {
      this.triggers.forEach((trigger) => {
        const panelIndex = trigger.dataset.mphTrigger;

        trigger.addEventListener('pointerenter', () => {
          if (window.innerWidth >= 1280 && !this.classList.contains('mph--compact')) this.openMega(panelIndex);
        });

        trigger.addEventListener('focus', () => {
          if (window.innerWidth >= 1280 && !this.classList.contains('mph--compact')) this.openMega(panelIndex);
        });
      });

      this.querySelectorAll('[data-mph-standalone]').forEach((link) => {
        link.addEventListener('pointerenter', () => this.closeMega());
        link.addEventListener('focus', () => this.closeMega());
      });

      if (this.bar) {
        this.bar.addEventListener('pointerenter', () => this.cancelMegaClose());
        this.bar.addEventListener('pointerleave', () => this.scheduleMegaClose());
      }

      if (this.hoverBridge) {
        this.hoverBridge.addEventListener('pointerenter', () => this.cancelMegaClose());
        this.hoverBridge.addEventListener('pointerleave', () => this.scheduleMegaClose());
      }

      this.panels.forEach((panel) => {
        panel.addEventListener('pointerenter', () => this.cancelMegaClose());
        panel.addEventListener('pointerleave', () => this.scheduleMegaClose());
      });
    }

    bindTabs() {
      this.panels.forEach((panel) => {
        panel.querySelectorAll('[data-mph-tab-trigger]').forEach((trigger) => {
          const activate = () => this.activateTab(panel, trigger.dataset.mphTabTrigger);
          trigger.addEventListener('pointerenter', activate);
          trigger.addEventListener('focus', activate);
          trigger.addEventListener('click', activate);
        });
      });
    }

    bindSearch() {
      this.searchButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (this.searchStage?.classList.contains('is-open')) {
            this.closeSearch();
          } else {
            this.openSearch();
          }
        });
      });

      this.querySelectorAll('[data-mph-search-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeSearch());
      });

      this.searchInput?.addEventListener('input', () => this.schedulePredictiveSearch());
      this.searchInput?.addEventListener('focus', () => {
        const query = this.searchInput.value.trim();
        if (query.length >= 3) this.getPredictiveSearchResults(query);
      });
      this.searchInput?.addEventListener('keydown', (event) => this.handlePredictiveKeydown(event));
      this.searchOutput?.addEventListener('pointerover', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) this.selectPredictiveOption(option);
      });
    }

    bindMobileDrawer() {
      if (this.drawerButton) {
        this.drawerButton.addEventListener('click', () => this.openDrawer(this.drawerButton));
      }

      this.querySelectorAll('[data-mph-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer());
      });

      this.drawer?.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => this.closeDrawer(false));
      });
    }

    bindGlobalEvents() {
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        if (this.classList.contains('is-mobile-open')) {
          this.closeDrawer();
          return;
        }

        if (this.searchStage?.classList.contains('is-open')) {
          this.closeSearch();
          return;
        }

        this.closeMega();
      });

      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) {
          this.closeMega();
          this.closeSearch();
        }
      });

      window.addEventListener('resize', () => {
        this.updateNavigationFit();
        this.closeMega();
        this.closeSearch();
        if (window.innerWidth >= 1280 && !this.classList.contains('mph--compact')) this.closeDrawer(false);
      });
    }

    openMega(panelIndex) {
      const panel = this.panels.find((item) => item.dataset.mphPanel === String(panelIndex));
      if (!panel) return;

      this.cancelMegaClose();
      this.closeSearch();
      this.megaStage?.classList.add('is-open');

      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(trigger.dataset.mphTrigger === String(panelIndex)));
      });

      this.panels.forEach((item) => {
        const active = item === panel;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
      });
    }

    scheduleMegaClose() {
      this.cancelMegaClose();
      this.closeTimer = window.setTimeout(() => this.closeMega(), 220);
    }

    cancelMegaClose() {
      if (!this.closeTimer) return;
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    closeMega() {
      this.cancelMegaClose();
      this.megaStage?.classList.remove('is-open');
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((panel) => {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
      });
    }

    activateTab(panel, tabIndex) {
      panel.querySelectorAll('[data-mph-tab-trigger]').forEach((trigger) => {
        const active = trigger.dataset.mphTabTrigger === String(tabIndex);
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-selected', String(active));
      });

      panel.querySelectorAll('[data-mph-tab-panel]').forEach((tabPanel) => {
        const active = tabPanel.dataset.mphTabPanel === String(tabIndex);
        tabPanel.classList.toggle('is-active', active);
        tabPanel.setAttribute('aria-hidden', String(!active));
        if (active) this.loadBrandPanel(tabPanel);
      });
    }

    async loadBrandPanel(panel) {
      const source = panel.dataset.mphBrandSource;
      if (!source || panel.dataset.mphBrandLoaded === 'true' || this.brandPanelLoads.has(source)) return;

      const products = panel.querySelector('.mph-products');
      if (!products) return;
      products.setAttribute('aria-busy', 'true');
      const limit = Math.min(12, Number(panel.dataset.mphProductLimit) || 12);
      const load = (async () => {
        const cards = [];
        const productIds = new Set();
        const visited = new Set();
        let nextUrl = new URL(source, window.location.origin);
        nextUrl.searchParams.set('preview_theme_id', this.dataset.themeId);
        const view = nextUrl.searchParams.get('view');

        while (nextUrl && cards.length < limit && visited.size < 100) {
          if (nextUrl.origin !== window.location.origin || visited.has(nextUrl.href)) break;
          visited.add(nextUrl.href);
          const response = await fetch(nextUrl.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error('Menu products could not be loaded');
          const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
          const result = markup.querySelector('[data-mph-brand-response]');
          if (!result) throw new Error('Menu product response was empty');
          result.querySelectorAll('.mph-product').forEach((card) => {
            const key = card.dataset.productId;
            if (cards.length >= limit || productIds.has(key)) return;
            productIds.add(key);
            if (panel.dataset.mphShowPrice !== 'true') card.querySelector('.mph-product__price')?.remove();
            if (panel.dataset.mphShowBadges !== 'true') card.querySelector('.mph-product__badge')?.remove();
            cards.push(card);
          });
          if (result.dataset.mphNextUrl && cards.length < limit) {
            nextUrl = new URL(result.dataset.mphNextUrl, window.location.origin);
            nextUrl.searchParams.set('view', view);
            nextUrl.searchParams.set('preview_theme_id', this.dataset.themeId);
          } else {
            nextUrl = null;
          }
        }

        if (cards.length) products.replaceChildren(...cards);
        panel.dataset.mphBrandLoaded = 'true';
      })();

      this.brandPanelLoads.set(source, load);
      try {
        await load;
      } catch (_) {
        panel.dataset.mphBrandLoaded = 'false';
      } finally {
        products.setAttribute('aria-busy', 'false');
        this.brandPanelLoads.delete(source);
      }
    }

    openSearch() {
      if (!this.searchStage) return;
      this.closeMega();
      this.searchStage.classList.add('is-open');
      this.searchStage.setAttribute('aria-hidden', 'false');
      this.searchButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      window.setTimeout(() => {
        this.searchInput?.focus();
        const query = this.searchInput?.value.trim() || '';
        if (query.length >= 3) this.getPredictiveSearchResults(query);
      }, 80);
    }

    closeSearch() {
      if (!this.searchStage) return;
      window.clearTimeout(this.searchTimer);
      this.searchAbortController?.abort();
      this.searchStage.classList.remove('is-open');
      this.searchStage.setAttribute('aria-hidden', 'true');
      this.searchButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.closePredictiveSearch();
    }

    schedulePredictiveSearch() {
      window.clearTimeout(this.searchTimer);
      const query = this.searchInput?.value.trim() || '';

      if (query.length < 3) {
        this.searchAbortController?.abort();
        this.closePredictiveSearch(true);
        return;
      }

      this.searchTimer = window.setTimeout(() => this.getPredictiveSearchResults(query), 220);
    }

    async getPredictiveSearchResults(query) {
      if (!this.searchStage || !this.searchResults || !this.searchOutput || query.length < 3) return;

      const cacheKey = query.toLocaleLowerCase();
      if (this.searchCache.has(cacheKey)) {
        this.renderPredictiveSearch(this.searchCache.get(cacheKey));
        return;
      }

      this.searchAbortController?.abort();
      this.searchAbortController = new AbortController();
      this.showPredictiveLoading();

      const requestUrl = new URL(this.searchStage.dataset.mphPredictiveUrl, window.location.origin);
      requestUrl.searchParams.set('q', query);
      requestUrl.searchParams.set('resources[type]', 'product');
      requestUrl.searchParams.set('resources[limit]', '10');
      requestUrl.searchParams.set('resources[options][unavailable_products]', 'last');
      requestUrl.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      requestUrl.searchParams.set('section_id', 'modern-pill-predictive-search');

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: this.searchAbortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);

        const text = await response.text();
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const responseContent = documentFragment.querySelector('[data-mph-predictive-response]');
        if (!responseContent) throw new Error('Predictive search response was empty');

        const markup = responseContent.outerHTML;
        this.searchCache.set(cacheKey, markup);

        if (this.searchInput?.value.trim().toLocaleLowerCase() === cacheKey) {
          this.renderPredictiveSearch(markup);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.closePredictiveSearch(true);
      }
    }

    showPredictiveLoading() {
      const loading = document.createElement('p');
      loading.className = 'mph__results-loading';
      loading.textContent = this.searchStage.dataset.mphLoadingText || '';
      this.searchOutput.replaceChildren(loading);
      this.searchResults.hidden = false;
      this.searchResults.setAttribute('aria-busy', 'true');
      this.searchInput?.setAttribute('aria-expanded', 'true');
      if (this.searchStatus) this.searchStatus.textContent = loading.textContent;
    }

    renderPredictiveSearch(markup) {
      this.searchOutput.innerHTML = markup;
      this.searchResults.hidden = false;
      this.searchResults.setAttribute('aria-busy', 'false');
      this.searchInput?.setAttribute('aria-expanded', 'true');
      const status = this.searchOutput.querySelector('[data-mph-predictive-status]');
      if (this.searchStatus) this.searchStatus.textContent = status?.textContent.trim() || '';
    }

    closePredictiveSearch(clear = false) {
      if (!this.searchResults) return;
      this.searchResults.hidden = true;
      this.searchResults.setAttribute('aria-busy', 'false');
      this.searchInput?.setAttribute('aria-expanded', 'false');
      this.searchInput?.removeAttribute('aria-activedescendant');
      if (this.searchStatus) this.searchStatus.textContent = '';
      if (clear) this.searchOutput?.replaceChildren();
    }

    handlePredictiveKeydown(event) {
      if (!this.searchResults || this.searchResults.hidden) return;

      const options = [...this.searchOutput.querySelectorAll('[role="option"]')];
      if (!options.length) return;

      const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = selectedIndex < 0
          ? direction > 0 ? 0 : options.length - 1
          : (selectedIndex + direction + options.length) % options.length;
        this.selectPredictiveOption(options[nextIndex]);
      } else if (event.key === 'Enter' && selectedIndex >= 0) {
        event.preventDefault();
        options[selectedIndex].querySelector('a')?.click();
      }
    }

    selectPredictiveOption(selectedOption) {
      this.searchOutput.querySelectorAll('[role="option"]').forEach((option) => {
        option.setAttribute('aria-selected', String(option === selectedOption));
      });
      this.searchInput?.setAttribute('aria-activedescendant', selectedOption.id);
      selectedOption.scrollIntoView({ block: 'nearest' });
    }

    openDrawer(trigger) {
      if (!this.drawer) return;
      this.closeSearch();
      this.lastDrawerTrigger = trigger;
      this.classList.add('is-mobile-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('mph-lock');
      window.setTimeout(() => this.drawer.querySelector('[data-mph-drawer-close]')?.focus(), 100);
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawer) return;
      this.classList.remove('is-mobile-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('mph-lock');
      if (restoreFocus) this.lastDrawerTrigger?.focus();
    }
  }

  if (!customElements.get('modern-pill-header')) {
    customElements.define('modern-pill-header', ModernPillHeader);
  }
})();
